import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { keys, noop, isNil } from 'lodash';
import animateScrollTo from 'animated-scroll-to';
import { branch, withProps } from 'recompose';

import propTypeStyleBody from 'styling/config/editorBlock/PropTypes/styleBody';

import SurveyTakerBlock from '../SurveyTakerBlock';
import LoadingView from '../LoadingView';
import PoweredBy from '../PoweredBy';
import TakerMessageComponent from '../TakerMessageComponent';
import TakerError from '../TakerError';
import SectionHeader from '../sectionControls/SectionHeader';
import SurveyTakerPageContainer from '../SurveyTakerPageContainer';
import SurveyTakerPageBox from '../SurveyTakerPageBox';
import Captcha from '../Captcha';
import CaptchaVerifyMessage from '../CaptchaVerifyMessage';
import SurveyCompletedMessage from '../SurveyCompletedMessage';

import getElementToDocumentScrollOffset from '../../domains/common/utils/getElementToDocumentScrollOffset';
import { getScrollItemNode } from '../../utils/scroll/scrollElementUtils';
import filterPageContentBlocks from '../../utils/filterPageContentBlocks';
import filterPageBreakBlocks from '../../utils/filterPageBreakBlocks';

import coordinatesPropTypes from '../../config/coordinatesPropTypes';
import headerPageBoxTypes from '../../config/sectionHeader/headerPageBoxTypes';

import isPageLoadedInIframe from '../../utils/surveyEmbedding/isPageLoadedInIframe';
import {
  headerLogoSizePresetPropTypes,
  sizePropTypes,
} from '../../config/sectionHeader/sectionHeaderPropTypes';
import TakerBlockBox from '../TakerBlockBox';
import PageBreakBox from '../PageBreakBox';

import styles from './SurveyTakerPage.scss';

const DEFAULT_SCROLL_DURATION = 250;

class SurveyTakerPage extends Component {
  static propTypes = {
    error: PropTypes.shape({
      reason: PropTypes.node,
      icon: PropTypes.string,
      message: PropTypes.node,
    }),
    backgroundColor: PropTypes.string,
    blockList: PropTypes.array.isRequired,
    currentPage: PropTypes.number,
    currentDisplayPage: PropTypes.number,
    // @TODO provide PropTypes.shape once we have schema to answers
    answerList: PropTypes.object,
    onAnswer: PropTypes.func.isRequired,
    onErrorScrollComplete: PropTypes.func.isRequired,
    firstInvalidFormName: PropTypes.string,
    headerBackgroundImageUrl: PropTypes.string,
    headerBackgroundCoords: coordinatesPropTypes,
    headerBackgroundImageSize: PropTypes.string,
    headerLogoImageUrl: PropTypes.string,
    headerLogoImageAlign: PropTypes.string,
    headerLogoWithTextAlign: PropTypes.string,
    headerHeight: PropTypes.number,
    headerBackgroundColor: PropTypes.string,
    isHeaderFullWidth: PropTypes.bool,
    isHeaderPlaceholder: PropTypes.bool,
    isFullWidth: PropTypes.bool,
    shouldRenderPoweredBy: PropTypes.bool,
    shouldRenderCaptcha: PropTypes.bool,
    isCaptchaVerified: PropTypes.bool,
    isCaptchaPending: PropTypes.bool,
    captchaVerifyCallback: PropTypes.func,
    className: PropTypes.string,
    initApp: PropTypes.func,
    pageBoxType: PropTypes.oneOf(keys(headerPageBoxTypes)),
    isLandingCurrentSection: PropTypes.bool,
    hasSurveyQuestionsSectionOnlyOnePage: PropTypes.bool,
    isSurveyCompleted: PropTypes.bool,
    isContentEnabled: PropTypes.bool,
    headerLogoImageLinkUrl: PropTypes.string,
    headerLogoImageSizePreset: headerLogoSizePresetPropTypes,
    headerLogoImageSize: sizePropTypes,
    headerLayout: PropTypes.string,
    headerTextContent: PropTypes.object,
    headerClientUuid: PropTypes.string,
    isInPreviewMode: PropTypes.bool,
    isHeaderEnabled: PropTypes.bool,
    questionNumberingEnabled: PropTypes.bool,
    styleBody: propTypeStyleBody,
    shouldShowQuestionBlock: PropTypes.func,
    isConditionalShowLogicQuestion: PropTypes.func,
    getConditionalShowLogicQuestionVisibility: PropTypes.func,
    updateConditionalShowLogicQuestionState: PropTypes.func,
  };

  static defaultProps = {
    initApp: noop,
    onAnswer: noop,
    onErrorScrollComplete: noop,
    currentPage: null,
    isSurveyCompleted: false,
    isInPreviewMode: false,
    backgroundColor: '#ffffff',
    isHeaderEnabled: true,
    shouldShowQuestionBlock: () => true,
    isConditionalShowLogicQuestion: () => false,
    getConditionalShowLogicQuestionVisibility: () => false,
    updateConditionalShowLogicQuestionState: () => {},
  };

  state = { captchaFailed: false };

  componentDidMount() {
    const { initApp } = this.props;
    initApp();
  }

  componentDidUpdate(prevProps) {
    const { currentPage, firstInvalidFormName, isSurveyCompleted } = this.props;
    const shouldScrollToForm =
      firstInvalidFormName && firstInvalidFormName !== prevProps.firstInvalidFormName;

    if (shouldScrollToForm) {
      this.scrollToForm(firstInvalidFormName);
    }

    const hasPageChanged = !isNil(currentPage) && currentPage !== prevProps.currentPage;
    const shouldScrollToTop = hasPageChanged || isSurveyCompleted !== prevProps.isSurveyCompleted;

    if (shouldScrollToTop) {
      animateScrollTo(0, {
        maxDuration: DEFAULT_SCROLL_DURATION,
      });
    }
  }

  scrollToForm = (formName) => {
    const scrollToElement = getScrollItemNode(formName);
    if (!scrollToElement) {
      return;
    }
    animateScrollTo(getElementToDocumentScrollOffset(scrollToElement, -100), {
      maxDuration: DEFAULT_SCROLL_DURATION,
      onComplete: this.props.onErrorScrollComplete,
    });
  };

  isPageEmpty = () => this.props.blockList.length === 0;

  getPageNumber = () => {
    const { currentDisplayPage, isLandingCurrentSection, hasSurveyQuestionsSectionOnlyOnePage } = this.props;

    if (hasSurveyQuestionsSectionOnlyOnePage || isLandingCurrentSection) {
      return null;
    } else if (!isNil(currentDisplayPage)) {
      return currentDisplayPage;
    }
  };

  renderBlock = (block) => {
    const {
      answerList,
      onAnswer,
      isInPreviewMode,
      questionNumberingEnabled,
      shouldShowQuestionBlock,
      isConditionalShowLogicQuestion,
      getConditionalShowLogicQuestionVisibility,
      updateConditionalShowLogicQuestionState,
    } = this.props;

    let showQuestionBlock = true;

    if (!isInPreviewMode) {
      showQuestionBlock = shouldShowQuestionBlock(block.clientUuid);
    }

    if (!isInPreviewMode) {
      if (
        isConditionalShowLogicQuestion(block.clientUuid) &&
        getConditionalShowLogicQuestionVisibility(block.clientUuid) !== showQuestionBlock
      ) {
        updateConditionalShowLogicQuestionState(block.clientUuid);
      }
    }

    return showQuestionBlock ? (
      <TakerBlockBox key={`question-${block.id}`} block={block}>
        <SurveyTakerBlock
          questionNumber={questionNumberingEnabled ? block.questionNumber : null}
          block={block}
          answer={answerList[block.id]}
          isInPreviewMode={isInPreviewMode}
          onAnswer={onAnswer}
          pageNumber={this.getPageNumber()}
        />
      </TakerBlockBox>
    ) : null;
  };

  renderPageBreak = (pageBreak) => (
    <PageBreakBox key={pageBreak.id} className={styles.pageBreakContainer}>
      {this.renderBlock(pageBreak)}
    </PageBreakBox>
  );

  renderBlockList = () => {
    const { blockList } = this.props;
    if (this.isPageEmpty()) {
      return null;
    }
    const pageContentBlocks = filterPageContentBlocks(blockList);
    return <div>{pageContentBlocks.map(this.renderBlock)}</div>;
  };

  shouldRenderPageBreak = () => {
    const { isSurveyCompleted, shouldRenderCaptcha } = this.props;

    return !isSurveyCompleted && !this.isPageEmpty() && !shouldRenderCaptcha;
  };

  shouldShowHeader = () => {
    const {
      error,
      isHeaderEnabled,
      isHeaderPlaceholder,
      isSurveyCompleted,
      shouldRenderCaptcha,
    } = this.props;

    return isHeaderEnabled && !isSurveyCompleted && !error && !isHeaderPlaceholder && !shouldRenderCaptcha;
  };

  renderContent = () => {
    const { error, isSurveyCompleted } = this.props;

    if (error && error.componentType) {
      return <TakerMessageComponent componentType={error.componentType} />;
    }

    if (isSurveyCompleted) {
      return (
        <div className={styles.content}>
          <SurveyCompletedMessage />
        </div>
      );
    }

    return <div className={styles.content}>{error ? <TakerError {...error} /> : this.renderBlockList()}</div>;
  };

  renderHeader() {
    const {
      headerBackgroundImageUrl,
      headerBackgroundImageSize,
      headerLogoImageUrl,
      headerLogoImageAlign,
      headerLogoWithTextAlign,
      headerHeight,
      headerBackgroundColor,
      isHeaderFullWidth,
      headerBackgroundCoords,
      isContentEnabled,
      headerLogoImageLinkUrl,
      headerLogoImageSizePreset,
      headerLogoImageSize,
      headerLayout,
      headerTextContent,
      headerClientUuid,
    } = this.props;

    return (
      <div className={cx({ [styles.headerWrapper]: !isHeaderFullWidth })}>
        <SectionHeader
          isHeaderFullWidth={isHeaderFullWidth}
          backgroundImageUrl={headerBackgroundImageUrl}
          backgroundImageSize={headerBackgroundImageSize}
          logoImageUrl={headerLogoImageUrl}
          logoImageAlign={headerLogoImageAlign}
          logoWithTextAlign={headerLogoWithTextAlign}
          height={headerHeight}
          backgroundColor={headerBackgroundColor}
          backgroundCoords={headerBackgroundCoords}
          isContentEnabled={isContentEnabled}
          logoImageLinkUrl={headerLogoImageLinkUrl}
          logoImageSizePreset={headerLogoImageSizePreset}
          logoImageSize={headerLogoImageSize}
          layout={headerLayout}
          textContent={headerTextContent}
          clientUuid={headerClientUuid}
        />
      </div>
    );
  }

  setCaptchaStatus = (captchaFailed) => this.setState({ captchaFailed });

  renderCaptcha = () => {
    const { isCaptchaVerified, isCaptchaPending, captchaVerifyCallback } = this.props;

    const { captchaFailed } = this.state;

    return (
      <div>
        {!captchaFailed && isCaptchaPending && <LoadingView />}
        {!captchaFailed && (
          <CaptchaVerifyMessage isCaptchaVerified={isCaptchaVerified} isCaptchaPending={isCaptchaPending} />
        )}
        <Captcha captchaVerifyCallback={captchaVerifyCallback} mistrustedCallback={this.setCaptchaStatus} />
      </div>
    );
  };

  renderPoweredBy = () => (
    <div className={styles.footerContainer}>
      <div className={styles.emptyPushBlock} />
      <PoweredBy className={styles.footer} />
    </div>
  );

  render() {
    const {
      className,
      isFullWidth,
      isHeaderFullWidth,
      shouldRenderCaptcha,
      blockList,
      isSurveyCompleted,
      shouldRenderPoweredBy,
      isInPreviewMode,
      styleBody,
    } = this.props;

    const pageBoxType = this.shouldShowHeader() ? this.props.pageBoxType : '';

    return (
      <SurveyTakerPageContainer
        className={cx({ [styles.withHeaderOffset]: isInPreviewMode }, className)}
        isFullWidth={isFullWidth}
        style={styleBody}
      >
        {this.shouldShowHeader() && this.renderHeader()}
        <SurveyTakerPageBox
          isFullWidth={isFullWidth}
          isHeaderFullWidth={isHeaderFullWidth}
          isSurveyCompleted={isSurveyCompleted}
          pageBoxType={pageBoxType}
        >
          {shouldRenderCaptcha ? this.renderCaptcha() : this.renderContent()}
        </SurveyTakerPageBox>
        {this.shouldRenderPageBreak() && filterPageBreakBlocks(blockList).map(this.renderPageBreak)}
        {shouldRenderPoweredBy && this.renderPoweredBy()}
      </SurveyTakerPageContainer>
    );
  }
}

export default branch(
  isPageLoadedInIframe,
  withProps({
    isFullWidth: true,
    isHeaderFullWidth: true,
    pageBoxType: headerPageBoxTypes.trimmedTop,
  }),
)(SurveyTakerPage);
